import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface MultiSelectProps<T> {
	value: T[];
	onChange: (next: T[]) => void;
	options: T[];
	getKey: (item: T) => string;
	getLabel: (item: T) => string;
	/** Custom sync filter; defaults to case-insensitive substring on the label. */
	searchFn?: (item: T, query: string) => boolean;
	placeholder?: string;
	emptyMessage?: string;
	loading?: boolean;
	disabled?: boolean;
	/** Async loader for paginated / remote search (debounced 250ms). */
	loadOptions?: (query: string) => Promise<T[]>;
	renderChip?: (item: T) => ReactNode;
	renderOption?: (item: T) => ReactNode;
}

const DEBOUNCE_MS = 250;

/**
 * Multi-value picker with chip rendering, search filtering and a dropdown panel.
 * The single most-reused composite in the editor — render slots let one component
 * serve product / category / customer / site / manufacturer pickers.
 */
export function MultiSelect<T>({
	value,
	onChange,
	options,
	getKey,
	getLabel,
	searchFn,
	placeholder,
	emptyMessage = 'Δεν βρέθηκαν αποτελέσματα',
	loading,
	disabled,
	loadOptions,
	renderChip,
	renderOption
}: MultiSelectProps<T>) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [asyncOptions, setAsyncOptions] = useState<T[]>([]);
	const [asyncLoading, setAsyncLoading] = useState(false);

	// Keep the latest loadOptions without forcing callers to memoize it.
	const loadRef = useRef(loadOptions);
	loadRef.current = loadOptions;

	// Debounced async load (only when a loader is provided and the panel is open).
	useEffect(() => {
		if (!loadRef.current || !open) return;
		let cancelled = false;
		setAsyncLoading(true);
		const handle = setTimeout(() => {
			loadRef
				.current?.(query)
				.then(res => {
					if (!cancelled) setAsyncOptions(res);
				})
				.finally(() => {
					if (!cancelled) setAsyncLoading(false);
				});
		}, DEBOUNCE_MS);
		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [query, open]);

	const selectedKeys = new Set(value.map(getKey));
	const source = loadOptions ? asyncOptions : options;
	const available = (
		loadOptions ? source : source.filter(o => (searchFn ? searchFn(o, query) : getLabel(o).toLowerCase().includes(query.toLowerCase())))
	).filter(o => !selectedKeys.has(getKey(o)));

	const add = (item: T) => onChange([...value, item]);
	const remove = (key: string) => onChange(value.filter(v => getKey(v) !== key));

	const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' && query === '' && value.length > 0) {
			remove(getKey(value[value.length - 1]));
		}
	};

	const isLoading = loading || asyncLoading;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled}>
				<div
					className={cn(
						'flex min-h-9 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1.5 text-sm transition-colors',
						'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
						disabled && 'cursor-not-allowed opacity-50'
					)}>
					{value.map(item => {
						const key = getKey(item);
						return (
							<span key={key} className="inline-flex items-center gap-1 rounded-md bg-muted py-0.5 pl-2 pr-1 text-xs text-foreground">
								{renderChip ? renderChip(item) : getLabel(item)}
								<button
									type="button"
									onClick={e => {
										e.stopPropagation();
										remove(key);
									}}
									className="rounded-sm text-foreground-muted hover:text-foreground"
									aria-label="remove">
									<X size={12} />
								</button>
							</span>
						);
					})}
					{value.length === 0 && <span className="px-1 text-foreground-subtle">{placeholder}</span>}
				</div>
			</PopoverTrigger>
			<PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
				<Command shouldFilter={false}>
					<CommandInput value={query} onValueChange={setQuery} placeholder={placeholder} onKeyDown={onInputKeyDown} />
					<CommandList>
						{isLoading ? (
							<div className="flex items-center justify-center gap-2 py-6 text-sm text-foreground-muted">
								<Loader2 size={14} className="animate-spin" /> …
							</div>
						) : (
							<>
								<CommandEmpty>{emptyMessage}</CommandEmpty>
								<CommandGroup>
									{available.map(item => {
										const key = getKey(item);
										return (
											<CommandItem
												key={key}
												value={key}
												onSelect={() => {
													add(item);
													setQuery('');
												}}>
												{renderOption ? renderOption(item) : getLabel(item)}
											</CommandItem>
										);
									})}
								</CommandGroup>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
